/**
 * Clean URLs. Two modes, chosen at build time (APP_URL_MODE, see src/config/backend.ts):
 *
 *   hash (default)  https://host/#/b/smadar         exactly what the app has always used
 *   path            https://host/b/smadar           History API; needs the SPA fallback in public/_redirects (Cloudflare Pages)
 *
 * Both modes read both URL shapes, so old links keep working: a path build converts `/#/b/smadar` to `/b/smadar` (replaceState, no
 * reload), a hash build converts `/b/smadar` to `/#/b/smadar`. Internally every router works with a "route string" such as
 * `/b/smadar/time?sheet=x`; this module is the only place that knows how that maps to a URL.
 *
 * The pure functions (routeOf, hrefOf, normalizeUrl...) take a location-like object and are unit tested without a DOM. The DOM binding
 * (bindPathMode) intercepts same-origin `<a href="#/...">` clicks and `location.hash = '#/...'` assignments so the ~25 screens that
 * still write hash links work unchanged in path mode.
 */
import { BUILD_URL_MODE, type UrlMode } from './backend';

export type { UrlMode } from './backend';
export interface LocLike { pathname: string; search: string; hash: string }

/** First path segments the app owns. Anything else on a path URL is not ours (a static file, a 404...). */
export const ROUTE_ROOTS = ['b', 'm', 'o', 'app', 'demo', 'privacy', 'accessibility', 'terms', 'support', 'delete-account'] as const;
const ROOT_RE = new RegExp(`^/(${ROUTE_ROOTS.join('|')})(/|$|\\?)`);

let mode: UrlMode = BUILD_URL_MODE;
export const urlMode = (): UrlMode => mode;
/** Tests only. */
export function setUrlModeForTests(next: UrlMode): void { mode = next; }

/** Route string ('/b/x?y=1') of a location in the given mode. Empty hash / path means the landing page '/'. */
export function routeOf(loc: LocLike, m: UrlMode = mode): string {
  if (m === 'hash') {
    const h = loc.hash.replace(/^#/, '');
    return h ? (h.startsWith('/') ? h : `/${h}`) : '/';
  }
  const path = loc.pathname.replace(/\/{2,}/g, '/').replace(/(.)\/+$/, '$1') || '/';
  return `${path === '/index.html' ? '/' : path}${loc.search}`;
}

/** The raw value routers keep as `Route.raw`: hash mode keeps the leading '#', exactly like location.hash did. */
export function rawOf(loc: LocLike, m: UrlMode = mode): string {
  return m === 'hash' ? (loc.hash || '#/') : routeOf(loc, m);
}

/** href for a route string. */
export function hrefOf(route: string, m: UrlMode = mode): string {
  const r = route.startsWith('#') ? route.slice(1) : route;
  const withSlash = r.startsWith('/') ? r : `/${r}`;
  return m === 'hash' ? `#${withSlash}` : withSlash;
}

/** `#/b/x?y` -> `/b/x?y`; undefined when the hash is not a route (anchors like `#main`, empty). */
export function legacyHashToRoute(hash: string): string | undefined {
  return /^#\//.test(hash) ? hash.slice(1) : undefined;
}

/**
 * If the current URL is in the other mode's shape, the URL to replace it with (relative to the origin); otherwise undefined.
 *  path mode : '/#/b/x' -> '/b/x'      (old links, QR codes and bookmarks keep working)
 *  hash mode : '/b/x'   -> '/#/b/x'    (a path link opened on a hash build)
 */
export function normalizeUrl(loc: LocLike, m: UrlMode = mode, keepSearch = true): string | undefined {
  if (m === 'path') {
    const route = legacyHashToRoute(loc.hash);
    if (!route) return undefined;
    // On load, /#/b/x?sheet=1 plus an existing ?native=1 query are merged. For later in-app navigations (keepSearch=false) the
    // old query belongs to the previous route and is dropped; only a query written in the target itself survives.
    const [p, q = ''] = route.split('?');
    const search = [keepSearch ? loc.search.replace(/^\?/, '') : '', q].filter(Boolean).join('&');
    return `${p}${search ? `?${search}` : ''}`;
  }
  if (ROOT_RE.test(loc.pathname) && !loc.hash) return `/#${loc.pathname}${loc.search}`;
  return undefined;
}

/** True when the current URL is a public customer page (booking, manage, offer). */
export const isPublicRoute = (route: string): boolean => /^\/(b|m|o)\//.test(route);

// ------------------------------------------------------------------ DOM binding
export const ROUTE_EVENT = 'planella:route';
const hasDom = (): boolean => typeof window !== 'undefined' && typeof history !== 'undefined';

/** Current route string of the real location. */
export function currentRoute(): string { return hasDom() ? routeOf(location) : '/'; }
/** Current raw value (see rawOf) of the real location. */
export function currentRaw(): string { return hasDom() ? rawOf(location) : mode === 'hash' ? '#/' : '/'; }

/** Tells every router the URL changed (pushState/replaceState do not fire events by themselves). */
export function announceRoute(): void { if (hasDom()) window.dispatchEvent(new Event(ROUTE_EVENT)); }

/** Subscribe to any URL change: hashchange, popstate and our own pushState announcements. */
export function onRouteChange(cb: () => void): () => void {
  window.addEventListener('hashchange', cb);
  window.addEventListener('popstate', cb);
  window.addEventListener(ROUTE_EVENT, cb);
  return () => { window.removeEventListener('hashchange', cb); window.removeEventListener('popstate', cb); window.removeEventListener(ROUTE_EVENT, cb); };
}

/** Moves to a route string in the active mode. Works in both modes; `state` is stored on the history entry. */
export function go(route: string, opts: { replace?: boolean; state?: unknown } = {}): void {
  if (!hasDom()) return;
  const target = hrefOf(route);
  if (mode === 'hash') {
    if (opts.state !== undefined) { if (opts.replace) history.replaceState(opts.state, '', target); else history.pushState(opts.state, '', target); announceRoute(); return; }
    if (opts.replace) location.replace(target); else location.hash = target;
    return;
  }
  if (target === routeOf(location)) return;
  if (opts.replace) history.replaceState(opts.state ?? null, '', target); else history.pushState(opts.state ?? null, '', target);
  announceRoute();
}

let bound = false;
/**
 * Call once at startup. In path mode: converts a legacy `#/...` URL, converts later `#/...` navigations (anchors, location.hash
 * assignments) and turns clicks on internal links into pushState. In hash mode it only converts a path URL to its hash form.
 */
export function bindUrlMode(): void {
  if (!hasDom() || bound) return;
  bound = true;
  const fix = (keepSearch = true): boolean => {
    const next = normalizeUrl(location, mode, keepSearch);
    if (next === undefined) return false;
    history.replaceState(history.state, '', next);
    return true;
  };
  const changed = fix();
  if (mode === 'hash') { if (changed) announceRoute(); return; }
  window.addEventListener('hashchange', () => { if (fix(false)) announceRoute(); });
  document.addEventListener('click', (e) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const a = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
    if (!a || a.target === '_blank' || a.hasAttribute('download')) return;
    const href = a.getAttribute('href') ?? '';
    const route = href.startsWith('#/') ? href.slice(1) : ROOT_RE.test(href) || href === '/' ? href : undefined;
    if (route === undefined) return;
    e.preventDefault();
    go(route);
  });
}
